import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * V2 CreatorsFilterBar — Search + filters for the creators list page.
 *
 *  - text search (display_name / tiktok_username / whatsapp)
 *  - status (all/active/inactive)
 *  - server (all/MENA/FR+/IT+/DE+/US+ — comes from props.servers)
 *  - tier (cleartext text — comes from current_month tier_current values)
 *  - manager (admin only — props.managers list)
 *
 * Adaptive : displays only the filters whose option list is non-empty.
 *
 * Props :
 *  - filters: { search, status, server, tier, managerId }
 *  - servers, tiers, managers, showManagerFilter
 *  - onChange(field, value)
 */
const STATUS_OPTIONS = [
  { id: '',         labelKey: 'creatorsV2.filters.statusAll',      label: 'All' },
  { id: 'active',   labelKey: 'creatorsV2.filters.statusActive',   label: 'Active' },
  { id: 'inactive', labelKey: 'creatorsV2.filters.statusInactive', label: 'Inactive' },
  { id: 'suspended',labelKey: 'creatorsV2.filters.statusSuspended', label: 'Suspended' },
];

const CreatorsFilterBar = ({
  filters,
  servers = [],
  tiers = [],
  managers = [],
  showManagerFilter = true,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl bg-card border border-border p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {/* Search */}
      <div className="lg:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-1">
          {t('creatorsV2.filters.search', 'Search')}
        </label>
        <input
          type="text"
          value={filters.search || ''}
          onChange={(e) => onChange('search', e.target.value)}
          placeholder={t('creatorsV2.filters.searchPlaceholder', 'name, @username, phone…')}
          className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Status */}
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-1">
          {t('creatorsV2.filters.status', 'Status')}
        </label>
        <select
          value={filters.status || ''}
          onChange={(e) => onChange('status', e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>{t(o.labelKey, o.label)}</option>
          ))}
        </select>
      </div>

      {/* Server */}
      {servers.length > 0 && (
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-1">
            {t('creatorsV2.filters.server', 'Server')}
          </label>
          <select
            value={filters.server || ''}
            onChange={(e) => onChange('server', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('creatorsV2.filters.serverAll', 'All servers')}</option>
            {servers.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      )}

      {/* Tier */}
      {tiers.length > 0 && (
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-1">
            {t('creatorsV2.filters.tier', 'Tier')}
          </label>
          <select
            value={filters.tier || ''}
            onChange={(e) => onChange('tier', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('creatorsV2.filters.tierAll', 'All tiers')}</option>
            {tiers.map((t2) => (
              <option key={t2} value={t2}>T{t2}</option>
            ))}
          </select>
        </div>
      )}

      {/* Manager */}
      {showManagerFilter && managers.length > 0 && (
        <div className="lg:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-1">
            {t('creatorsV2.filters.manager', 'Manager')}
          </label>
          <select
            value={filters.managerId || ''}
            onChange={(e) => onChange('managerId', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('creatorsV2.filters.managerAll', 'All managers')}</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>{m.full_name || m.email}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default CreatorsFilterBar;
